import PopoverSelect from "@components/dropdowns/popoverSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/dropdowns/select";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { joinClassNames } from "@utils/helpers";
import { motion } from "framer-motion";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Dispatch, SetStateAction, useState } from "react";
import { preset_trigger_text } from "./dateFilter";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const weekdays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const getDaysInMonth = (month: number, year: number) =>
  new Date(year, month, 0).getDate();
const getWeekdayForDate = (date: Date) =>
  date.getDay() - 1 === -1 ? 6 : date.getDay() - 1;

function getShownDaysInMonth(month: number, year: number) {
  const shownDaysInMonth: {
    year: number;
    month: number;
    day: number;
    date_string: string;
  }[] = [];

  for (let i = 1; i <= getDaysInMonth(month, year); i++) {
    shownDaysInMonth.push({
      year,
      month,
      day: i,
      date_string: `${year}-${month.toString().padStart(2, "0")}-${i
        .toString()
        .padStart(2, "0")}`,
    });
  }

  return shownDaysInMonth;
}

interface Props {
  date: string;
  day: number;
  month: number;
  year: number;
  date1: string | null;
  date2: string | null;
  setDate: Dispatch<SetStateAction<[string | null, string | null]>>;
  selectedPreset: "today" | "tomorrow" | "weekend" | "week" | "custom";
  setSelectedPreset: Dispatch<
    SetStateAction<"today" | "tomorrow" | "weekend" | "week" | "custom">
  >;
  handleSubmit?: () => void;
}

export default function Calendar({
  date,
  day,
  month,
  year,
  date1,
  date2,
  setDate,
  selectedPreset,
  setSelectedPreset,
  handleSubmit,
}: Props) {
  const router = useRouter();
  const params = useSearchParams();
  const dateFrom = params.get("dateFrom");
  const dateTo = params.get("dateTo");
  const pathname = usePathname();
  const [[current_month, current_year], setCurrentMonthYear] = useState([
    month,
    year,
  ]);
  const [[potential_date1, potential_date2], setPotentialDate] = useState<
    [string | null, string | null]
  >([null, null]);

  const first_day_offset = getWeekdayForDate(
    new Date(
      `${current_year.toString().padStart(4, "0")}-${current_month
        .toString()
        .padStart(2, "0")}-01`
    )
  );
  const first_date = potential_date1 ?? date1;
  const second_date = potential_date2 ?? date2;

  function calculateDatesForPreset(
    preset: "today" | "tomorrow" | "weekend" | "week" | "custom"
  ): [string | null, string | null] {
    switch (preset) {
      case "today":
        return [date, date];
      case "tomorrow": {
        let [new_year, new_month, new_day] = [year, month, day + 1];
        if (getDaysInMonth(month, year) < new_day) {
          new_day = new_day - getDaysInMonth(month, year);
          new_month = month + 1;
        }
        if (new_month > 12) {
          new_month = 1;
          new_year = year + 1;
        }
        const new_date = `${new_year}-${new_month
          .toString()
          .padStart(2, "0")}-${new_day.toString().padStart(2, "0")}`;
        return [new_date, new_date];
      }
      case "weekend": {
        const distance_to_saturday = 5 - getWeekdayForDate(new Date(date));
        let [sat_year, sat_month, sat_day] = [
          year,
          month,
          day + distance_to_saturday,
        ];
        let [sun_year, sun_month, sun_day] = [
          year,
          month,
          day + distance_to_saturday + 1,
        ];

        if (getDaysInMonth(month, year) < sat_day) {
          sat_day = sat_day - getDaysInMonth(month, year);
          sun_day = sun_day - getDaysInMonth(month, year);
          sat_month = month + 1;
          sun_month = month + 1;
        }
        if (sat_month > 12) {
          sat_month = 1;
          sun_month = 1;
          sat_year = year + 1;
          sun_year = year + 1;
        }
        const sat_date = `${sat_year}-${sat_month
          .toString()
          .padStart(2, "0")}-${sat_day.toString().padStart(2, "0")}`;
        const sun_date = `${sun_year}-${sun_month
          .toString()
          .padStart(2, "0")}-${sun_day.toString().padStart(2, "0")}`;
        return [sat_date, sun_date];
      }
      case "week": {
        let [new_year, new_month, new_day] = [year, month, day + 6];
        if (getDaysInMonth(month, year) < new_day) {
          new_day = new_day - getDaysInMonth(month, year);
          new_month = month + 1;
        }
        if (new_month > 12) {
          new_month = 1;
          new_year = year + 1;
        }
        const new_date = `${new_year}-${new_month
          .toString()
          .padStart(2, "0")}-${new_day.toString().padStart(2, "0")}`;
        return [date, new_date];
      }
      case "custom":
        return [date1, date2];
    }
  }

  function incrementMonth(amount: number) {
    let new_month = current_month + amount;
    let new_year = current_year;

    if (new_month === 0) {
      new_month = 12;
      new_year = current_year - 1;
    }
    if (new_month === 13) {
      new_month = 1;
      new_year = current_year + 1;
    }

    setCurrentMonthYear([new_month, new_year]);
  }

  function calculateBorderRadius(date: {
    year: number;
    month: number;
    day: number;
    date_string: string;
  }) {
    const corners_to_round: Array<"tl" | "tr" | "bl" | "br"> = [];
    const week_ago = `${date.year}-${date.month.toString().padStart(2, "0")}-${(
      date.day - 7
    )
      .toString()
      .padStart(2, "0")}`;
    const week_ahead = `${date.year}-${date.month
      .toString()
      .padStart(2, "0")}-${(date.day + 7).toString().padStart(2, "0")}`;

    if (!first_date || !second_date) return "";

    const weekday = getWeekdayForDate(new Date(date.date_string));

    if (weekdays[weekday] === "sunday" || date.date_string === second_date) {
      if (date.day <= 7 || week_ago < first_date) {
        corners_to_round.push("tr");
      }
      if (
        date.day > getDaysInMonth(current_month, current_year) - 7 ||
        week_ahead > second_date
      ) {
        corners_to_round.push("br");
      }
    }
    if (weekdays[weekday] === "monday" || date.date_string === first_date) {
      if (date.day <= 7 || week_ago < first_date) {
        corners_to_round.push("tl");
      }
      if (
        date.day > getDaysInMonth(current_month, current_year) - 7 ||
        week_ahead > second_date
      ) {
        corners_to_round.push("bl");
      }
    }

    return `${corners_to_round.includes("tl") ? "rounded-tl-md " : ""}${
      corners_to_round.includes("tr") ? "rounded-tr-md " : ""
    }${corners_to_round.includes("bl") ? "rounded-bl-md " : ""}${
      corners_to_round.includes("br") ? "rounded-br-md" : ""
    }`.trim();
  }

  return (
    <div className="flex flex-col gap-1 font-semibold">
      <div className="flex items-center text-lg font-bold">
        <motion.span
          initial={
            selectedPreset !== "custom"
              ? { opacity: 1, width: "auto" }
              : { opacity: 0, width: 0 }
          }
          animate={
            selectedPreset !== "custom"
              ? {
                  opacity: 1,
                  width: "auto",
                  transition: { opacity: { delay: 0.3 } },
                }
              : { opacity: 0, width: 0, transition: { width: { delay: 0.3 } } }
          }
          className="h-7"
        >
          Events on
          <div className="inline-block w-1" />
        </motion.span>
        <div className="sm:hidden">
          <PopoverSelect
            value={selectedPreset}
            display={
              selectedPreset === "custom"
                ? preset_trigger_text[selectedPreset]
                : preset_trigger_text[selectedPreset].split("Events on ")[1]
            }
            onOptionSelect={({ option }) => {
              if (
                option === "today" ||
                option === "tomorrow" ||
                option === "weekend" ||
                option === "week" ||
                option === "custom"
              ) {
                setDate(calculateDatesForPreset(option));
                setSelectedPreset(option);
              }
            }}
            options={[
              {
                value: "today",
                display:
                  selectedPreset === "custom" ? "Events on today" : "today",
              },
              {
                value: "tomorrow",
                display:
                  selectedPreset === "custom"
                    ? "Events on tomorrow"
                    : "tomorrow",
              },
              {
                value: "weekend",
                display:
                  selectedPreset === "custom"
                    ? "Events on this weekend"
                    : "this weekend",
              },
              {
                value: "week",
                display:
                  selectedPreset === "custom"
                    ? "Events on in the next 7 days"
                    : "in the next 7 days",
              },
              {
                value: "custom",
                display: "Custom range",
                is_hidden: selectedPreset !== "custom",
              },
            ]}
          />
        </div>
        <div className="hidden sm:inline-block">
          <Select
            defaultValue="today"
            value={selectedPreset}
            onValueChange={(
              value: "today" | "tomorrow" | "weekend" | "week" | "custom"
            ) => {
              setDate(calculateDatesForPreset(value));
              setSelectedPreset(value);
            }}
          >
            <SelectTrigger className="!h-9 !w-fit !text-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem className="!text-lg" value="today">
                {selectedPreset === "custom" ? "Events on " : ""}today
              </SelectItem>
              <SelectItem className="!text-lg" value="tomorrow">
                {selectedPreset === "custom" ? "Events on " : ""}tomorrow
              </SelectItem>
              <SelectItem className="!text-lg" value="weekend">
                {selectedPreset === "custom" ? "Events on " : ""}this weekend
              </SelectItem>
              <SelectItem className="!text-lg" value="week">
                {selectedPreset === "custom" ? "Events on " : ""}in the next 7
                days
              </SelectItem>
              <SelectItem
                value="custom"
                className={joinClassNames(
                  "!text-lg",
                  selectedPreset !== "custom" ? "hidden" : ""
                )}
              >
                Custom range
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-semibold text-black text-xl">
          {months[current_month - 1]} {current_year}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => incrementMonth(-1)}
            className="text-black p-1 rounded-md bg-white hover:bg-gray-50 transition-colors ease-in-out duration-300"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => incrementMonth(1)}
            className="text-black p-1 rounded-md bg-white hover:bg-gray-50 transition-colors ease-in-out duration-300"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div className="flex flex-col items-center w-full">
        <div className="grid grid-cols-7 w-fit">
          <span className="flex items-center justify-center h-9 w-9">M</span>
          <span className="flex items-center justify-center h-9 w-9">T</span>
          <span className="flex items-center justify-center h-9 w-9">W</span>
          <span className="flex items-center justify-center h-9 w-9">T</span>
          <span className="flex items-center justify-center h-9 w-9">F</span>
          <span className="flex items-center justify-center h-9 w-9">S</span>
          <span className="flex items-center justify-center h-9 w-9">S</span>
        </div>
        <div className="grid grid-cols-7 w-fit">
          {Array.from({ length: first_day_offset }).map((_, index) => (
            <span key={index} className="h-9 w-9" />
          ))}
          {getShownDaysInMonth(current_month, current_year).map((cal_day) => (
            <button
              key={cal_day.date_string}
              onMouseEnter={() => {
                if (date1 && !date2) {
                  if (cal_day.date_string < date1) {
                    setPotentialDate([cal_day.date_string, date1]);
                  } else {
                    setPotentialDate([date1, cal_day.date_string]);
                  }
                }
              }}
              onMouseLeave={() => {
                setPotentialDate([null, null]);
              }}
              onClick={() => {
                setSelectedPreset("custom");
                setDate((prev) => {
                  if (prev[0] && !prev[1]) {
                    const new_date1 =
                      prev[0] < cal_day.date_string
                        ? prev[0]
                        : cal_day.date_string;
                    const new_date2 =
                      new_date1 === prev[0] ? cal_day.date_string : prev[0];
                    return [new_date1, new_date2];
                  }
                  return [cal_day.date_string, null];
                });
              }}
              className={joinClassNames(
                "flex items-center justify-center h-9 w-9 transition-all ease-in-out duration-300",
                cal_day.date_string < date ? "text-gray-400" : "",
                first_date &&
                  second_date &&
                  cal_day.date_string >= first_date &&
                  cal_day.date_string <= second_date
                  ? joinClassNames(
                      calculateBorderRadius(cal_day),
                      "bg-indigo-100 text-indigo-800"
                    )
                  : date1 && !date2 && cal_day.date_string === date1
                  ? "bg-indigo-100 text-indigo-800 rounded-md"
                  : "enabled:hover:bg-indigo-50 enabled:hover:rounded-md"
              )}
              disabled={cal_day.date_string < date}
            >
              {cal_day.day}
            </button>
          ))}
        </div>
      </div>
      {date1 !== dateFrom || date2 !== dateTo || !date1 || !date2 ? (
        <button
          className="mt-2 w-full py-2 rounded-lg bg-indigo-500 disabled:bg-indigo-200 text-white hover:bg-indigo-400 transition-colors ease-in-out duration-300"
          onClick={() => {
            if (date1 && date2) {
              const current_params = new URLSearchParams(
                Array.from(params.entries())
              );
              const timezone_offset = (
                new Date().getTimezoneOffset() * -1
              ).toString();

              current_params.set("dateFrom", date1);
              current_params.set("dateTo", date2);
              current_params.set("timeZoneOffset", timezone_offset);
              const query = current_params.toString();

              if (handleSubmit) {
                handleSubmit();
              }

              router.push(`${pathname}${query ? `?${query}` : ""}`);
            }
          }}
          disabled={!date1 || !date2}
        >
          Apply
        </button>
      ) : (
        <button
          className="mt-2 w-full py-2 rounded-lg bg-red-500 text-white hover:bg-red-400 transition-colors ease-in-out duration-300"
          onClick={() => {
            setDate(calculateDatesForPreset(selectedPreset));
            const current_params = new URLSearchParams(
              Array.from(params.entries())
            );
            current_params.delete("dateFrom");
            current_params.delete("dateTo");
            const query = current_params.toString();

            if (handleSubmit) {
              handleSubmit();
            }

            router.push(`${pathname}${query ? `?${query}` : ""}`);
          }}
        >
          Clear filter
        </button>
      )}
    </div>
  );
}
